import { useId } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

// The getdocs three-box data-flow diagram as inline SVG: side by side on wide
// screens, stacked on phones. Text is wrapped here because SVG can't wrap.
const useStyles = makeStyles({
  wide: { display: 'none', width: '100%', height: 'auto', '@media (min-width: 720px)': { display: 'block' } },
  tall: { display: 'block', width: '100%', maxWidth: '400px', height: 'auto', margin: '0 auto', '@media (min-width: 720px)': { display: 'none' } },
  box: { fill: tokens.colorNeutralBackground1, stroke: tokens.colorNeutralStroke1, strokeWidth: 1.5 },
  boxEnd: { stroke: tokens.colorCompoundBrandStroke, strokeWidth: 2 },
  title: { fill: 'var(--tellec-heading)', fontWeight: 700 },
  text: { fill: tokens.colorNeutralForeground2 },
  arrow: { stroke: tokens.colorBrandForeground1, strokeWidth: 2, fill: 'none' },
  head: { fill: tokens.colorBrandForeground1 },
  label: { fill: tokens.colorBrandForeground1, fontWeight: 600 },
});

interface Box {
  title: string;
  text: string;
}

const PAD = 20;
const TITLE = { size: 17, line: 22 };
const BODY = { size: 14.5, line: 20 };

function wrap(text: string, maxChars: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/)) {
    if (line && (line + ' ' + word).length > maxChars) {
      lines.push(line);
      line = word;
    } else {
      line = line ? `${line} ${word}` : word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function layout(box: Box, width: number) {
  const title = wrap(box.title, Math.floor((width - 2 * PAD) / 9));
  const body = wrap(box.text, Math.floor((width - 2 * PAD) / 7.2));
  const height = PAD + title.length * TITLE.line + 8 + body.length * BODY.line + PAD - 4;
  return { title, body, height };
}

function BoxShape({ box, x, y, width, height, last }: { box: ReturnType<typeof layout>; x: number; y: number; width: number; height: number; last: boolean }) {
  const s = useStyles();
  const titleTop = y + PAD + TITLE.size;
  const bodyTop = y + PAD + box.title.length * TITLE.line + 8 + BODY.size;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx={8} className={last ? `${s.box} ${s.boxEnd}` : s.box} />
      <text className={s.title} fontSize={TITLE.size}>
        {box.title.map((l, i) => (
          <tspan key={i} x={x + PAD} y={titleTop + i * TITLE.line}>
            {l}
          </tspan>
        ))}
      </text>
      <text className={s.text} fontSize={BODY.size}>
        {box.body.map((l, i) => (
          <tspan key={i} x={x + PAD} y={bodyTop + i * BODY.line}>
            {l}
          </tspan>
        ))}
      </text>
    </g>
  );
}

export function DataFlowDiagram({ boxes, label }: { boxes: Box[]; label: string }) {
  const s = useStyles();
  // useId can contain characters that break url(#...) references.
  const id = 'flow' + useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const summary = boxes.map((b) => b.title).join(', then ') + `. Only ${label} flows between them.`;

  // Wide: boxes in a row, arrows between.
  const W = 960;
  const gap = 120;
  const bw = (W - gap * (boxes.length - 1)) / boxes.length;
  const wide = boxes.map((b) => layout(b, bw));
  const wh = Math.max(...wide.map((b) => b.height));

  // Tall: boxes stacked, arrows down.
  const TW = 340;
  const tgap = 64;
  const tall = boxes.map((b) => layout(b, TW));
  const tops = tall.reduce<number[]>((acc, _b, i) => [...acc, i === 0 ? 0 : acc[i - 1] + tall[i - 1].height + tgap], []);
  const th = tops[tops.length - 1] + tall[tall.length - 1].height + 2;

  return (
    <>
      <svg className={s.wide} viewBox={`-2 -2 ${W + 4} ${wh + 4}`} role="img" aria-labelledby={`${id}w`}>
        <title id={`${id}w`}>{summary}</title>
        <defs>
          <marker id={`${id}h`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" className={s.head} />
          </marker>
        </defs>
        {wide.map((b, i) => {
          const x = i * (bw + gap);
          return (
            <g key={i}>
              <BoxShape box={b} x={x} y={0} width={bw} height={wh} last={i === boxes.length - 1} />
              {i < boxes.length - 1 && (
                <>
                  <line x1={x + bw + 10} y1={wh / 2} x2={x + bw + gap - 10} y2={wh / 2} className={s.arrow} markerEnd={`url(#${id}h)`} />
                  <text x={x + bw + gap / 2} y={wh / 2 - 12} textAnchor="middle" fontSize={13} className={s.label}>
                    {label}
                  </text>
                </>
              )}
            </g>
          );
        })}
      </svg>

      <svg className={s.tall} viewBox={`-2 -2 ${TW + 4} ${th + 2}`} role="img" aria-labelledby={`${id}t`}>
        <title id={`${id}t`}>{summary}</title>
        <defs>
          <marker id={`${id}v`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" className={s.head} />
          </marker>
        </defs>
        {tall.map((b, i) => (
          <g key={i}>
            <BoxShape box={b} x={0} y={tops[i]} width={TW} height={b.height} last={i === boxes.length - 1} />
            {i < boxes.length - 1 && (
              <>
                <line x1={TW / 2} y1={tops[i] + b.height + 8} x2={TW / 2} y2={tops[i + 1] - 8} className={s.arrow} markerEnd={`url(#${id}v)`} />
                <text x={TW / 2 + 14} y={tops[i] + b.height + tgap / 2 + 5} fontSize={13} className={s.label}>
                  {label}
                </text>
              </>
            )}
          </g>
        ))}
      </svg>
    </>
  );
}
